import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown,
  BarChart3, 
  ArrowRight, 
  Zap, 
  Repeat, 
  Layers, 
  Clock, 
  DollarSign, 
  ChevronRight, 
  Globe, 
  Star, 
  ArrowUpRight, 
  ArrowDownRight,
  ArrowDownLeft,
  Bitcoin,
  LineChart,
  PieChart,
  Activity,
  Newspaper,
  ExternalLink,
  ChevronLeft
} from 'lucide-react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { MarketData, NewsItem, PortfolioSnapshot, DatabaseUserAsset } from '../hooks/useDatabase';
import { FuturesPosition, Transaction, TradingMode } from '../App';
import { useDatabase } from '../hooks/useDatabase';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface HomePageProps {
  usdtBalance: number;
  btcBalance: number;
  currentBtcPrice: number;
  marketData: MarketData[];
  futuresPositions: FuturesPosition[];
  transactions: Transaction[];
  setTradingMode: (mode: TradingMode) => void;
  newsItems: NewsItem[];
  totalPortfolioValue?: number;
  userAssets?: DatabaseUserAsset[];
}

const HomePage: React.FC<HomePageProps> = ({
  usdtBalance,
  btcBalance,
  currentBtcPrice,
  marketData,
  futuresPositions,
  transactions,
  setTradingMode,
  newsItems,
  totalPortfolioValue = 0,
  userAssets = []
}) => {
  const { t } = useTranslation();
  const { fetchPortfolioSnapshots, portfolioSnapshots, createPortfolioSnapshot, coingeckoMarketCapData } = useDatabase();
  const [activeTab, setActiveTab] = useState<'overview' | 'positions' | 'transactions'>('overview');
  const [filteredSnapshots, setFilteredSnapshots] = useState<PortfolioSnapshot[]>([]);
  
  // News pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  
  // Chart period state
  const [chartPeriod, setChartPeriod] = useState<'1W' | '1M' | '3M' | '1Y' | 'All'>('1M');
  
  // Calculate actual portfolio value if not provided or is zero
  const actualPortfolioValue = totalPortfolioValue > 0 ? totalPortfolioValue : (usdtBalance + (btcBalance * currentBtcPrice));
  
  // Check if data is still loading
  const isDataLoading = actualPortfolioValue === 0 && (usdtBalance === 0 && btcBalance === 0 && currentBtcPrice === 0);
  
  // Calculate total PnL from futures positions
  const totalPositionsPnl = futuresPositions.reduce((sum, position) => {
    // Calculate correct unrealized PnL without extra leverage multiplication
    const entryPrice = position.entryPrice || position.entry_price || 0;
    const currentPrice = position.currentPrice || position.current_price || 0;
    const amount = position.amount || 0;
    const side = position.side || 'long';
    
    let unrealizedPnl = 0;
    if (side === 'long') {
      unrealizedPnl = (currentPrice - entryPrice) * amount;
    } else {
      unrealizedPnl = (entryPrice - currentPrice) * amount;
    }
    
    return sum + unrealizedPnl;
  }, 0);

  // Fetch portfolio snapshots based on selected period
  useEffect(() => {
    const fetchSnapshotsForPeriod = async () => {
      let days: number;
      
      switch (chartPeriod) {
        case '1W':
          days = 7;
          break;
        case '1M':
          days = 30;
          break;
        case '3M':
          days = 90;
          break;
        case '1Y':
          days = 365;
          break;
        case 'All':
        default:
          days = 9999; // A large number to get all snapshots
          break;
      }
      
      await fetchPortfolioSnapshots(days);
    };
    
    fetchSnapshotsForPeriod();
  }, [chartPeriod, fetchPortfolioSnapshots]);

  // Filter and format portfolio snapshots for the chart
  useEffect(() => {
    if (portfolioSnapshots.length === 0) {
      // If no snapshots, create a default one with current values
      const today = new Date();
      const defaultSnapshots = [{
        id: 'default',
        user_id: '',
        snapshot_date: today.toISOString().split('T')[0],
        total_value: totalPortfolioValue,
        usdt_balance: usdtBalance,
        btc_balance: btcBalance,
        btc_price: currentBtcPrice,
        created_at: today.toISOString()
      }];
      
      setFilteredSnapshots(defaultSnapshots);
      return;
    }
    
    // Filter snapshots based on selected period
    let filteredData = [...portfolioSnapshots];
    
    // Sort by date
    filteredData.sort((a, b) => 
      new Date(a.snapshot_date).getTime() - new Date(b.snapshot_date).getTime()
    );
    
    setFilteredSnapshots(filteredData);
  }, [portfolioSnapshots, totalPortfolioValue, usdtBalance, btcBalance, currentBtcPrice]);
  
  // Get top performing assets
  const getTopPerformingAssets = () => {
    return marketData
      .filter(data => data.price > 0 && data.price !== 104325.3716)
      .sort((a, b) => (b.change_24h ?? 0) - (a.change_24h ?? 0))
      .slice(0, 5);
  };
  
  // Get recent transactions
  const getRecentTransactions = () => {
    return transactions.slice(0, 5);
  };

  // Pagination logic for news
  const indexOfLastNews = currentPage * itemsPerPage;
  const indexOfFirstNews = indexOfLastNews - itemsPerPage;
  const currentNews = newsItems.slice(indexOfFirstNews, indexOfLastNews);
  const totalPages = Math.ceil(newsItems.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  // Calculate high, low, and change values for the selected period
  const calculateStats = () => {
    if (filteredSnapshots.length === 0) {
      return {
        high: actualPortfolioValue,
        low: actualPortfolioValue,
        change: 0,
        changePercentage: 0
      };
    }
    
    const values = filteredSnapshots.map(snapshot => snapshot.total_value);
    const high = Math.max(...values);
    const low = Math.min(...values);
    
    // Calculate change from first to last snapshot
    const firstValue = filteredSnapshots[0].total_value;
    const lastValue = filteredSnapshots[filteredSnapshots.length - 1].total_value;
    const change = lastValue - firstValue;
    const changePercentage = firstValue > 0 ? (change / firstValue) * 100 : 0;
    
    return { high, low, change, changePercentage };
  };
  
  const { high, low, change, changePercentage } = calculateStats();

  // Prepare chart data
  const chartData = {
    labels: filteredSnapshots.map(snapshot => {
      const date = new Date(snapshot.snapshot_date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Portfolio Value',
        data: filteredSnapshots.map(snapshot => snapshot.total_value),
        borderColor: 'rgba(59, 130, 246, 1)', // blue-500
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.4,
        fill: true
      }
    ]
  };
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        titleColor: 'rgba(255, 255, 255, 1)',
        bodyColor: 'rgba(148, 163, 184, 1)',
        borderColor: 'rgba(51, 65, 85, 0.5)',
        borderWidth: 1,
        padding: 10,
        displayColors: false,
        callbacks: {
          label: function(context: any) {
            return `$${context.raw.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: 'rgba(148, 163, 184, 1)',
          maxTicksLimit: 7
        }
      },
      y: {
        grid: {
          color: 'rgba(51, 65, 85, 0.1)'
        },
        ticks: {
          color: 'rgba(148, 163, 184, 1)',
          callback: function(value: any) {
            return `$${value.toLocaleString('en-US', { notation: 'compact', compactDisplay: 'short' })}`;
          }
        }
      }
    }
  };

  // Get period label for stats
  const getPeriodLabel = () => {
    switch (chartPeriod) {
      case '1W': return '1W';
      case '1M': return '1M';
      case '3M': return '3M';
      case '1Y': return '1Y';
      case 'All': return 'All Time';
      default: return '';
    }
  };

  // Sort market data by market cap for Market Overview section
  const getSortedMarketData = () => {
    // Create a map of market caps from coingeckoMarketCapData
    const marketCapMap = new Map();
    
    if (coingeckoMarketCapData && coingeckoMarketCapData.length > 0) {
      coingeckoMarketCapData.forEach(coin => {
        marketCapMap.set(coin.symbol.toUpperCase(), coin.market_cap);
      });
    }
    
    // Filter valid market data
    const validMarketData = marketData.filter(data => 
      data.price > 0 && 
      data.price !== 104325.3716 && // Filter out placeholder price
      data.symbol.endsWith('USDT') // Only include USDT pairs
    );
    
    // Sort by market cap (descending)
    return validMarketData.sort((a, b) => {
      // Extract base symbol (remove USDT)
      const aSymbol = a.symbol.replace('USDT', '').toLowerCase();
      const bSymbol = b.symbol.replace('USDT', '').toLowerCase();
      
      // Get market caps from map
      const aMarketCap = marketCapMap.get(aSymbol.toUpperCase()) || 0;
      const bMarketCap = marketCapMap.get(bSymbol.toUpperCase()) || 0;
      
      // Sort by market cap (descending)
      return bMarketCap - aMarketCap;
    }).slice(0, 5); // Take top 5 for display
  };

  return (
    <div className="p-8">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
          {t('home.welcomeTitle')}
        </h1>
        <p className="text-slate-400">
          {t('home.welcomeSubtitle')}
        </p>
      </div>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Wallet size={20} className="text-white" />
            </div>
            <div>
              <div className="text-slate-400">{getPeriodLabel()} {t('home.change')}</div>
              <div className="text-2xl font-bold text-white" translate="no">
                {isDataLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-lg">Loading...</span>
                  </div>
                ) : (
                  `$${actualPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="text-slate-400">{getPeriodLabel()} Change</div>
            <div className={changePercentage >= 0 ? "text-emerald-400" : "text-red-400"} translate="no">
              {isDataLoading ? (
                <span className="text-slate-500">--</span>
              ) : (
                `${changePercentage >= 0 ? '+' : ''}${changePercentage.toFixed(2)}%`
              )}
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <TrendingUp size={20} className="text-white" />
            </div>
            <div>
              <div className="text-slate-400 text-sm">Positions P&L</div>
              <div className={`text-2xl font-bold ${totalPositionsPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`} translate="no">
                {totalPositionsPnl >= 0 ? '+' : ''}{totalPositionsPnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="text-slate-400">Open Positions</div>
            <div className="text-white" translate="no">{futuresPositions.length}</div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/25">
              <Bitcoin size={20} className="text-white" />
            </div>
            <div>
              <div className="text-slate-400 text-sm">BTC Price</div>
              <div className="text-2xl font-bold text-white" translate="no">${currentBtcPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="text-slate-400">24h Change</div>
            <div className={`${(marketData.find(d => d.symbol === 'BTCUSDT')?.change_24h ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} translate="no">
              {(marketData.find(d => d.symbol === 'BTCUSDT')?.change_24h ?? 0) >= 0 ? '+' : ''}
              {(marketData.find(d => d.symbol === 'BTCUSDT')?.change_24h ?? 0).toFixed(2)}%
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/25">
              <BarChart3 size={20} className="text-white" />
            </div>
            <div>
              <div className="text-slate-400 text-sm">Trading Volume</div>
              <div className="text-2xl font-bold text-white" translate="no">$125,430.50</div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="text-slate-400">{getPeriodLabel()} Change</div>
            <div className="text-emerald-400" translate="no">+8.3%</div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Portfolio Chart & Quick Actions */}
        <div className="lg:col-span-2 space-y-8">
          {/* Portfolio Chart */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between mb-6">
              <h2 className="text-xl font-semibold text-white flex-shrink-0 mb-3 md:mb-0">Portfolio Performance</h2>
              <div className="flex gap-2 w-full md:w-auto overflow-x-auto hide-scrollbar">
                <button 
                  onClick={() => setChartPeriod('1W')}
                  className={`px-3 py-1 text-xs rounded-lg ${
                    chartPeriod === '1W' 
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                      : 'bg-blue-500/10 text-slate-400 hover:bg-blue-500/20 hover:text-blue-400 transition-colors'
                  }`}
                >1W</button>
                <button 
                  onClick={() => setChartPeriod('1M')}
                  className={`px-3 py-1 text-xs rounded-lg ${
                    chartPeriod === '1M' 
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                      : 'bg-blue-500/10 text-slate-400 hover:bg-blue-500/20 hover:text-blue-400 transition-colors'
                  }`}
                >1M</button>
                <button 
                  onClick={() => setChartPeriod('3M')}
                  className={`px-3 py-1 text-xs rounded-lg ${
                    chartPeriod === '3M' 
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                      : 'bg-blue-500/10 text-slate-400 hover:bg-blue-500/20 hover:text-blue-400 transition-colors'
                  }`}
                >3M</button>
                <button 
                  onClick={() => setChartPeriod('1Y')}
                  className={`px-3 py-1 text-xs rounded-lg ${
                    chartPeriod === '1Y' 
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                      : 'bg-blue-500/10 text-slate-400 hover:bg-blue-500/20 hover:text-blue-400 transition-colors'
                  }`}
                >1Y</button>
                <button 
                  onClick={() => setChartPeriod('All')}
                  className={`px-3 py-1 text-xs rounded-lg ${
                    chartPeriod === 'All'
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                      : 'bg-blue-500/10 text-slate-400 hover:bg-blue-500/20 hover:text-blue-400 transition-colors'
                  }`}
                >All</button>
              </div>
            </div>
            
            <div className="h-80">
              {filteredSnapshots.length > 1 ? (
                <div translate="no">
                  <Line data={chartData} options={chartOptions as any} />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-slate-500">
                    <p className="mb-2">Not enough data to display chart</p>
                    <p className="text-sm">Portfolio history will be tracked automatically</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-4 gap-4 mt-6 text-sm">
              <div>
                <div className="text-slate-400">Current Value</div>
                <div className="text-white font-medium" translate="no">${totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div className="text-slate-400">{getPeriodLabel()} High</div>
                <div className="text-white font-medium" translate="no">${high.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div className="text-slate-400">{getPeriodLabel()} Low</div>
                <div className="text-white font-medium" translate="no">${low.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div className="text-slate-400">{getPeriodLabel()} Change</div>
                <div className={changePercentage >= 0 ? "text-emerald-400" : "text-red-400"} translate="no">
                  {changePercentage >= 0 ? '+' : ''}{changePercentage.toFixed(2)}%
                </div>
              </div>
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <button 
              onClick={() => setTradingMode('swap')}
              className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl hover:border-blue-500/30 hover:shadow-blue-500/10 transition-all duration-300 group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Repeat size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Swap</h3>
                <p className="text-slate-400 text-sm">{t('home.swapDescription')}</p>
                <div className="mt-4 text-blue-400 flex items-center gap-1 text-sm">
                  <span>{t('home.tradeNow')}</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('futures')}
              className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl hover:border-emerald-500/30 hover:shadow-emerald-500/10 transition-all duration-300 group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25 mb-4 group-hover:scale-110 transition-transform duration-300">
                  <LineChart size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Futures</h3>
                <p className="text-slate-400 text-sm">{t('home.futuresDescription')}</p>
                <div className="mt-4 text-emerald-400 flex items-center gap-1 text-sm">
                  <span>{t('home.tradeNow')}</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('staking')}
              className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl hover:border-purple-500/30 hover:shadow-purple-500/10 transition-all duration-300 group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25 mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Layers size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Staking</h3>
                <p className="text-slate-400 text-sm">{t('home.stakingDescription')}</p>
                <div className="mt-4 text-purple-400 flex items-center gap-1 text-sm">
                  <span>{t('home.stakeNow')}</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('wallet')}
              className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl hover:border-amber-500/30 hover:shadow-amber-500/10 transition-all duration-300 group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25 mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Wallet size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Wallet</h3>
                <p className="text-slate-400 text-sm">{t('home.walletDescription')}</p>
                <div className="mt-4 text-amber-400 flex items-center gap-1 text-sm">
                  <span>{t('home.viewWallet')}</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </button>
          </div>
          
          {/* News Section */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Newspaper size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">Market News</h2>
            </div>
            
            <div className="space-y-6">
              {currentNews.map((news) => (
                <div key={news.id} className="flex gap-4">
                  {news.image_url && (
                    <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={news.image_url} alt={news.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        news.category === 'crypto' ? 'bg-orange-500/20 text-orange-400' :
                        news.category === 'markets' ? 'bg-emerald-500/20 text-emerald-400' :
                        news.category === 'economy' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-purple-500/20 text-purple-400'
                      }`}>
                        {news.category.charAt(0).toUpperCase() + news.category.slice(1)}
                      </span>
                      <span className="text-xs text-slate-400">
                        {new Date(news.published_at).toLocaleString(undefined, { 
                          hour: 'numeric', 
                          minute: 'numeric',
                          day: 'numeric',
                          month: 'short'
                        })}
                      </span>
                    </div>
                    <h3 className="font-semibold text-white mb-1">{news.title}</h3>
                    <p className="text-sm text-slate-400 line-clamp-2">{news.summary}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-slate-500">{news.source}</span>
                      <a href={news.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors text-xs flex items-center gap-1">
                        <span>Read More</span>
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
              
              {newsItems.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Newspaper size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No news available</p>
                  <p className="text-sm text-slate-600 mt-2">Check back later for market updates</p>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {newsItems.length > 0 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700/50">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    currentPage === 1
                      ? 'text-slate-500 cursor-not-allowed'
                      : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                  }`}
                >
                  <ChevronLeft size={16} />
                  {t('home.previous')}
                </button>
                
                <div className="text-sm text-slate-400">
                  {t('home.pageInfo', { current: currentPage, total: totalPages })}
                </div>
                
                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    currentPage === totalPages
                      ? 'text-slate-500 cursor-not-allowed'
                      : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                  }`}
                >
                  {t('home.next')}
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Right Column - Assets, Positions, Transactions */}
        <div className="space-y-8">
          {/* Tabs */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setActiveTab('overview')}
                className={`text-xs md:text-sm font-medium px-2 md:px-4 py-1 md:py-2 rounded-lg transition-all duration-300 ${
                  activeTab === 'overview'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('positions')}
                className={`text-xs md:text-sm font-medium px-2 md:px-4 py-1 md:py-2 rounded-lg transition-all duration-300 ${
                  activeTab === 'positions'
                    ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/25'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                Positions
              </button>
              <button
                onClick={() => setActiveTab('transactions')}
                className={`text-xs md:text-sm font-medium px-2 md:px-4 py-1 md:py-2 rounded-lg transition-all duration-300 ${
                  activeTab === 'transactions'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/25'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                Transactions
              </button>
            </div>
            
            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Asset Allocation</h3>
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-32 h-32">
                    <PieChart size={128} className="text-blue-400" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-slate-300">USDT</span>
                      </div>
                      <div className="text-white font-medium">
                        {actualPortfolioValue > 0 ? ((usdtBalance / actualPortfolioValue) * 100).toFixed(2) : '0.00'}%
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                        <span className="text-slate-300">BTC</span>
                      </div>
                      <div className="text-white font-medium">
                        {actualPortfolioValue > 0 ? ((btcBalance * currentBtcPrice / actualPortfolioValue) * 100).toFixed(2) : '0.00'}%
                      </div>
                    </div>
                    {userAssets && userAssets.filter(asset => 
                      asset.asset_symbol !== 'USDT' && 
                      asset.asset_symbol !== 'BTC' && 
                      asset.balance > 0
                    ).map((asset, index) => {
                      // Find price for this asset
                      const assetMarketData = marketData.find(data => data.symbol === `${asset.asset_symbol}USDT`);
                      const price = assetMarketData?.price || 0;
                      const value = asset.balance * price;
                      const percentage = actualPortfolioValue > 0 ? (value / actualPortfolioValue) * 100 : 0;
                      
                      // Get a color based on index
                      const colors = ['blue', 'purple', 'indigo', 'pink', 'red'];
                      const colorIndex = index % colors.length;
                      
                      return (
                        <div key={asset.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 bg-${colors[colorIndex]}-500 rounded-full`}></div>
                            <span className="text-slate-300">{asset.asset_symbol}</span>
                          </div>
                          <div className="text-white font-medium">{percentage.toFixed(2)}%</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold text-white mb-4">Top Performing Assets</h3>
                <div className="space-y-3">
                  {getTopPerformingAssets().map((asset, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-900/40 rounded-xl hover:bg-slate-900/60 transition-all duration-300">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                          <span className="text-white font-bold">{asset.symbol.replace('USDT', '').substring(0, 2)}</span>
                        </div>
                        <div>
                          <div className="font-medium text-white">{asset.symbol.replace('USDT', '')}</div>
                          <div className="text-xs text-slate-400" translate="no">${asset.price.toFixed(2)}</div>
                        </div>
                      </div>
                      <div className="text-emerald-400 flex items-center gap-1" translate="no">
                        <TrendingUp size={16} />
                        <span>+{(asset.change_24h ?? 0).toFixed(2)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {activeTab === 'positions' && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Open Positions</h3>
                {futuresPositions.length > 0 ? (
                  <div className="space-y-3">
                    {futuresPositions.map((position) => (
                      <div key={position.id} className="p-3 bg-slate-900/40 rounded-xl hover:bg-slate-900/60 transition-all duration-300">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium">{position.symbol}</span>
                
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              position.side === 'long' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                            }`} translate="no">
                              {position.side.toUpperCase()}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400" translate="no">
                              {position.leverage}x
                            </span>
                          </div>
                          <div className={`font-medium ${position.unrealizedPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`} translate="no">
                            {position.unrealizedPnl >= 0 ? '+' : ''}{position.unrealizedPnl.toFixed(2)} USDT
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs" translate="no">
                          <div>
                            <span className="text-slate-400">Entry Price</span>
                            <div className="text-white">${position.entryPrice.toFixed(2)}</div>
                          </div>
                          <div>
                            <span className="text-slate-400">Current Price</span>
                            <div className="text-white">${position.currentPrice.toFixed(2)}</div>
                          </div>
                          <div>
                            <span className="text-slate-400">ROI</span>
                            <div className={position.roi >= 0 ? 'text-emerald-400' : 'text-red-400'} translate="no">
                              {position.roi >= 0 ? '+' : ''}{position.roi.toFixed(2)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <button 
                      onClick={() => setTradingMode('futures')}
                      className="w-full mt-4 text-blue-400 hover:text-blue-300 transition-colors flex items-center justify-center gap-1 text-sm"
                    >
                      <span>View All Positions</span>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Activity size={48} className="mx-auto mb-4 opacity-50" />
                    <p className="mb-2">No open positions</p>
                    <button 
                      onClick={() => setTradingMode('futures')}
                      className="mt-2 text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Start Trading
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'transactions' && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Recent Transactions</h3>
                {transactions.length > 0 ? (
                  <div className="space-y-3">
                    {getRecentTransactions().map((transaction) => (
                      <div key={transaction.id} className="flex items-center justify-between p-3 bg-slate-900/40 rounded-xl hover:bg-slate-900/60 transition-all duration-300">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            transaction.type === 'deposit' ? 'bg-emerald-500/20' : 
                            transaction.type === 'withdrawal' ? 'bg-red-500/20' : 
                            'bg-blue-500/20'
                          }`}>
                            {transaction.type === 'deposit' ? (
                              <ArrowDownLeft size={16} className="text-emerald-400" />
                            ) : transaction.type === 'withdrawal' ? (
                              <ArrowUpRight size={16} className="text-red-400" />
                            ) : (
                              <Repeat size={16} className="text-blue-400" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-white">{transaction.description}</div>
                            <div className="text-xs text-slate-400" translate="no">{transaction.timestamp}</div>
                          </div>
                        </div>
                        <div className={`font-medium ${transaction.amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`} translate="no">
                          {transaction.amount >= 0 ? '+' : ''}{transaction.amount.toFixed(2)}
                        </div>
                      </div>
                    ))}
                    
                    <button 
                      onClick={() => setTradingMode('wallet')}
                      className="w-full mt-4 text-blue-400 hover:text-blue-300 transition-colors flex items-center justify-center gap-1 text-sm"
                    >
                      <span>View All Transactions</span>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Clock size={48} className="mx-auto mb-4 opacity-50" />
                    <p className="mb-2">No recent transactions</p>
                    <button 
                      onClick={() => setTradingMode('wallet')}
                      className="mt-2 text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Go to Wallet
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Market Overview */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25">
                <Globe size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">{t('sidebar.marketOverview')}</h2>
            </div>
            
            <div className="space-y-3">
              {getSortedMarketData().map((asset, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-900/40 rounded-xl hover:bg-slate-900/60 transition-all duration-300">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                      <span className="text-white font-bold">{asset.symbol.replace('USDT', '').substring(0, 2)}</span>
                    </div>
                    <div>
                      <div className="font-medium text-white">{asset.symbol.replace('USDT', '')}/USDT</div>
                      <div className="text-xs text-slate-400" translate="no">${asset.price.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className={`flex items-center gap-1 ${(asset.change_24h ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} translate="no">
                    {(asset.change_24h ?? 0) >= 0 ? (
                      <TrendingUp size={16} />
                    ) : (
                      <TrendingDown size={16} />
                    )}
                    <span>{(asset.change_24h ?? 0) >= 0 ? '+' : ''}{((asset.change_24h ?? 0)).toFixed(2)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;